import React, { memo, useEffect, useMemo, useState } from "react";

import { Button, Form, Image, Input, Tooltip, Typography } from "antd";
import PropTypes from "prop-types";
import { IoStar } from "react-icons/io5";
import { MdDownload } from "react-icons/md";

import Loader from "../../../Components/Loader/Loader";
import { useAppTheme } from "../../../context/ThemeProvider";
import { usePrivateDocuments } from "../../../hooks";
import { HELP_CONTENT, IMAGE_MAX_SIZE } from "../userKYCTypes";

const uploadWrapperInnerStyle = {
  width: IMAGE_MAX_SIZE,
  height: IMAGE_MAX_SIZE,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: 8,
  backgroundColor: "var(--background)",
  overflow: "hidden",
};

const FormItemWithImageUploader = ({
  kyc,
  title,
  subtitle,
  enableNumberInput = false,
  inputName,
  documentType,
  required = false,
  sides,
  rejectionReason,
  onAddRejectionReason,
}) => {
  const { COLORS } = useAppTheme();

  const [filesState, setFilesState] = useState({});

  const getImageKey = (side) => `${documentType}${side.toTitleCase()}Image`;

  const { isLoading, files, previewUrls } = usePrivateDocuments(
    useMemo(
      () => ({
        userId: kyc?._id,
        documentIds: [kyc?.[documentType]?.frontImage || ""].filter(Boolean),
        hideToast: true,
      }),
      [kyc, documentType]
    )
  );

  useEffect(() => {
    if (previewUrls.length > 0) {
      setFilesState((prev) => {
        if (prev.front && previewUrls?.[0] && prev.front === previewUrls?.[0]) {
          return prev;
        }

        return { front: previewUrls?.[0] };
      });
    }
  }, [previewUrls]);

  return (
    <div className="form-item-with-uploader">
      <Typography.Title
        level={5}
        className="title"
        style={{ display: "flex", alignItems: "center" }}
      >
        {title || HELP_CONTENT?.[documentType]?.title}

        {required && (
          <IoStar
            style={{
              fontSize: 10,
              color: COLORS.ERROR,
              marginLeft: 4,
            }}
          />
        )}
      </Typography.Title>

      <Typography.Paragraph className="subtitle">
        {subtitle || HELP_CONTENT?.[documentType]?.subtitle}
      </Typography.Paragraph>

      <div className="form-fields">
        <Input.TextArea
          placeholder={`Enter rejection reason for ${
            title || HELP_CONTENT?.[documentType]?.title
          }`}
          value={rejectionReason}
          onChange={(e) => onAddRejectionReason(e.target.value)}
          autoSize={{ minRows: 2, maxRows: 4 }}
          status="error"
          style={{ marginBottom: 16 }}
        />

        {enableNumberInput && (
          <Form.Item
            className="tmFormItem"
            name={inputName}
            label={HELP_CONTENT?.[documentType]?.label || ""}
            required={required}
          >
            <Input
              placeholder={HELP_CONTENT?.[documentType]?.placeholder}
              size="large"
              className="tmFormInput"
              readOnly
            />
          </Form.Item>
        )}

        <div className="upload-img-wrapper">
          {sides.map((side) => {
            const url = filesState?.[side];
            const file = side === "front" ? files?.[0] : files?.[1];
            const fileUrl = file ? URL.createObjectURL(file) : "";

            return (
              <Form.Item
                key={getImageKey(side)}
                name={getImageKey(side)}
                className="upload-img"
              >
                <div className="upload-img__upload-wrapper">
                  {url ? (
                    <div style={uploadWrapperInnerStyle}>
                      <Image
                        src={url}
                        alt={`${documentType} ${side}`}
                        style={{
                          width: "100%",
                          height: "100%",
                          maxWidth: IMAGE_MAX_SIZE,
                          maxHeight: IMAGE_MAX_SIZE,
                          aspectRatio: "auto",
                          objectFit: "contain",
                        }}
                        className={`preview__image ${
                          file?.type === "application/pdf"
                            ? "preview__image--pdf"
                            : ""
                        }`}
                        preview={file?.type !== "application/pdf"}
                        onClick={() => {
                          if (file?.type === "application/pdf") {
                            window.open(fileUrl, "_blank");
                          }
                        }}
                      />

                      {file ? (
                        <div className="upload-img__upload-wrapper__actions">
                          <Tooltip title="Download" placement="top">
                            <Button
                              shape="circle"
                              icon={<MdDownload size={18} />}
                              id="backgroundImgButton"
                              className="upload-img__upload-wrapper__actions__action-btn"
                              // view file in a new tab withouth downloading
                              onClick={() => window.open(fileUrl, "_blank")}
                            />
                          </Tooltip>
                        </div>
                      ) : null}
                    </div>
                  ) : (
                    <div style={uploadWrapperInnerStyle}>
                      {isLoading ? (
                        <Loader type="spinner" />
                      ) : (
                        "No document uploaded"
                      )}
                    </div>
                  )}
                </div>
              </Form.Item>
            );
          })}
        </div>
      </div>
    </div>
  );
};

FormItemWithImageUploader.propTypes = {
  kyc: PropTypes.shape(),
  title: PropTypes.string,
  subtitle: PropTypes.string,
  enableNumberInput: PropTypes.bool,
  inputName: PropTypes.string,
  documentType: PropTypes.string,
  required: PropTypes.bool,
  sides: PropTypes.shape(),
  rejectionReason: PropTypes.string,
  onAddRejectionReason: PropTypes.func,
};

export default memo(FormItemWithImageUploader);
